import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { select, NgRedux } from '@angular-redux/store';

import { NotificationFacade } from '@ridango/components';
import { BaseComponent, RegisterWithEmailDTO, WidgetOptionsForEmailRegister } from '@ridango/core';
import { hasNumberValidator } from '@ridango/core/lib/validators/has-number.validator';

import { Observable } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TermsDialogComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog/terms-dialog.component';
import { take, takeUntil } from 'rxjs/operators';
import { TermsDialogMobileComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog-mobile/terms-dialog-mobile.component';

import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-signup-email-notw-form',
    templateUrl: './signup-email-notw-form.component.html'
})
export class SignupEmailNoTWFormComponent extends BaseComponent implements OnInit {
    @select(['application', 'highContrast']) highContrast$: Observable<boolean>;

    public _terms = null;
    public _registerForm: FormGroup;
    public registrationOptions = this.env.REGISTRATION;

    public _showPassword = false;
    private _termsAgreed = false;
    private _gdprAgreed = false;
    private _termsDialog: MatDialogRef<TermsDialogComponent | TermsDialogMobileComponent>;

    tokenVisible = false;
    siteKey: string;
    recaptchaValue = '';
    hide = true;

    @Input() options: WidgetOptionsForEmailRegister;
    @Output() registerEvent = new EventEmitter<RegisterWithEmailDTO>();

    constructor(
        @Inject('ENV') public env: Env,
        private ngRedux: NgRedux<any>,
        private notification: NotificationFacade,
        private matDialog: MatDialog,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this._terms = this.getTerms();

        this._registerForm = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            name: new FormControl('', [Validators.required]),
            password: new FormControl('', [Validators.required, Validators.minLength(6), hasNumberValidator]),
            terms: new FormControl(this._termsAgreed, Validators.required),
            gdpr: new FormControl(this._gdprAgreed, Validators.required)
        });

        if (!this.registrationOptions.CHECK_GDPR) {
            this._registerForm.removeControl('gdpr');
        }

        if (!this.registrationOptions.DISPLAY_FULL_NAME_FIELD) {
            this._registerForm.removeControl('name');
        }

        if (this.env?.RECAPTCHA?.SITEKEY) {
            this.siteKey = this.env.RECAPTCHA.SITEKEY;
        }
    }

    getTerms() {
        return this.ngRedux.getState().core.region.terms;
    }

    openTerms() {
        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((highContrast) => {
            const termsUrl = this.getTermsUrl(highContrast, this._terms.content_base_url, this.options.locale);

            if (this.env.IS_MOBILE_APP) {
                this._termsDialog = this.matDialog.open(TermsDialogMobileComponent, {
                    data: { termsUrl, withAgreeButton: true },
                    width: '90%',
                    maxWidth: '80vh',
                    autoFocus: true,
                    panelClass: 'terms-dialog-mobile-wrapper'
                });
            } else {
                this._termsDialog = this.matDialog.open(TermsDialogComponent, {
                    data: { termsUrl },
                    width: '100%',
                    maxWidth: '800px',
                    panelClass: 'dialog-wrapper'
                });
            }

            this._termsDialog
                .afterClosed()
                .pipe(take(1))
                .subscribe((termsAgreed) => this.agreeTerms(termsAgreed));
        });
    }

    toggleCheckbox(name: string) {
        if (name === 'terms') {
            this._termsAgreed = !this._termsAgreed;
            this._registerForm.controls['terms'].patchValue(this._termsAgreed);
        }
        if (name === 'gdpr') {
            this._gdprAgreed = !this._gdprAgreed;
            this._registerForm.controls['gdpr'].patchValue(this._gdprAgreed);
        }
    }

    agreeTerms(agreement: boolean) {
        this._termsAgreed = agreement;
        this._registerForm.controls['terms'].patchValue(this._termsAgreed);
        this.closeTerms();
    }

    closeTerms() {
        this._termsDialog.close();
    }

    togglePassword(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this._showPassword = !this._showPassword;
    }

    getTermsUrl(highContrast: boolean = false, baseUrl: string, locale: string) {
        let termsUrl = `${baseUrl}/${locale}/`;
        if (highContrast) {
            termsUrl += '#high-contrast';
        }
        return termsUrl;
    }

    register() {
        if (!this._termsAgreed && this.registrationOptions.CHECK_GDPR && !this._gdprAgreed) {
            this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
        } else {
            if (!this._termsAgreed) {
                this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
            }

            if (this.registrationOptions.CHECK_GDPR && !this._gdprAgreed) {
                this.notification.error(this.translate.instant('MUST_AGREE_GDPR'));
            }
        }

        if (this._registerForm.valid) {
            const payload = this._registerForm.getRawValue();
            payload.terms = this._terms;
            delete payload['gdpr'];
            this.registerEvent.emit(payload);
        }
    }

    resolved(captchaResponse: string): void {
        this.recaptchaValue = captchaResponse;
    }
}
